import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Group, TextInput, NumberInput, ActionIcon } from "@mantine/core";
import { IconGripVertical, IconTrash } from "@tabler/icons-react";
import { ServiceAdjustmentFormOption } from "../../types/serviceAdjustmentForms";
import styles from "./AdjustmentFormsTab.module.css";

interface DraggableAnswerOptionProps {
  option: ServiceAdjustmentFormOption;
  questionIndex: number;
  optionIndex: number;
  onOptionChange: (
    questionIndex: number,
    optionIndex: number,
    field: string,
    value: any,
  ) => void;
  onDeleteOption: (questionIndex: number, optionIndex: number) => void;
}

function DraggableAnswerOption({
  option,
  questionIndex,
  optionIndex,
  onOptionChange,
  onDeleteOption,
}: DraggableAnswerOptionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: option.option_id || `option-temp-${questionIndex}-${optionIndex}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.answerContainer} ${isDragging ? styles.dragging : ""}`}
    >
      <Group gap="xs" align="flex-start" wrap="nowrap">
        <div {...attributes} {...listeners} className={styles.dragHandle}>
          <IconGripVertical size={16} />
        </div>
        <TextInput
          placeholder="Answer text"
          value={option.answer}
          onChange={(e) =>
            onOptionChange(questionIndex, optionIndex, "answer", e.target.value)
          }
          style={{ flex: 1, minWidth: "120px" }}
          size="xs"
          required
        />
        <NumberInput
          placeholder="Price Δ"
          value={option.price_delta ?? ""}
          onChange={(value) =>
            onOptionChange(questionIndex, optionIndex, "price_delta", value)
          }
          prefix="$"
          allowNegative
          decimalScale={2}
          style={{ width: "100px" }}
          size="xs"
        />
        <NumberInput
          placeholder="Duration Δ"
          value={option.duration_delta_mins ?? ""}
          onChange={(value) =>
            onOptionChange(
              questionIndex,
              optionIndex,
              "duration_delta_mins",
              value,
            )
          }
          suffix=" min"
          allowNegative
          style={{ width: "110px" }}
          size="xs"
        />
        <ActionIcon
          color="red"
          variant="subtle"
          onClick={() => onDeleteOption(questionIndex, optionIndex)}
          size="sm"
          className={styles.deleteButton}
        >
          <IconTrash size={16} />
        </ActionIcon>
      </Group>
    </div>
  );
}

export default DraggableAnswerOption;
